/**
 * <secret-offers> — the countdown on the cart drawer's secret-offer panel.
 *
 * Markup: snippets/cart-secret-offers.liquid. The panel ships `hidden`; this
 * file is what decides it may be seen, which is the right way round — with the
 * script blocked or broken an offer that is supposed to expire stays shut
 * instead of running forever.
 *
 * ## The clock starts when the drawer is opened
 *
 * Not on page load. The drawer's markup is in the DOM of every page whether or
 * not anyone has looked at the cart, so a clock started at load would often be
 * half spent — or over — the first time it was read. Dawn puts `.active` on
 * `<cart-drawer>` when it opens, so that class is the signal; one observer
 * watches for it and disconnects as soon as it has fired.
 *
 * ## sessionStorage, not localStorage
 *
 * The end time has to survive a reload and a page change, which is the whole
 * point — but a new visit should get a new twenty minutes rather than find the
 * offer permanently burnt. That is a session, exactly.
 *
 * Storage can throw (private mode, blocked site data). A failed read is
 * treated as "no clock yet" and a failed write as "this one runs in memory
 * only" — the panel still works for the length of the page.
 *
 * ## One ticker, re-queried every second
 *
 * The drawer replaces its whole inner DOM on every cart change, so an element
 * reference does not survive and a per-element interval would leak one timer
 * per add. The interval instead queries the live document, which costs one
 * selector call a second and cannot go stale.
 */
(() => {
  if (customElements.get('secret-offers')) return;

  const KEY = 'anew:secret-offers-end';
  let endAt = 0;
  let ticker = null;
  let watching = false;

  const read = () => {
    try {
      return Number(sessionStorage.getItem(KEY)) || 0;
    } catch (e) {
      return 0;
    }
  };

  const write = (value) => {
    try {
      sessionStorage.setItem(KEY, String(value));
    } catch (e) {
      /* in-memory for this page only */
    }
  };

  const format = (ms) => {
    const total = Math.ceil(ms / 1000);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const panels = () => document.querySelectorAll('secret-offers');

  const tick = () => {
    const left = endAt - Date.now();
    const text = format(Math.max(left, 0));

    panels().forEach((panel) => {
      if (left <= 0) {
        panel.hidden = true;
        return;
      }
      panel.hidden = false;
      panel.querySelectorAll('[data-secret-countdown]').forEach((el) => {
        if (el.textContent !== text) el.textContent = text;
      });
    });

    if (left <= 0 && ticker) {
      clearInterval(ticker);
      ticker = null;
    }
  };

  const start = (minutes) => {
    if (!endAt) {
      endAt = Date.now() + minutes * 60000;
      write(endAt);
    }
    if (!ticker) ticker = setInterval(tick, 1000);
    tick();
  };

  // The drawer is open when Dawn has put `.active` on it. Checked once up
  // front, then watched — `attributes` only, so this does not wake on the
  // section re-render that replaces the drawer's contents.
  const isOpen = () => document.querySelector('cart-drawer.active') !== null;

  const watchForOpen = (minutes) => {
    if (watching) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    watching = true;
    const observer = new MutationObserver(() => {
      if (!isOpen()) return;
      observer.disconnect();
      watching = false;
      start(minutes);
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  class SecretOffers extends HTMLElement {
    connectedCallback() {
      const minutes = Number(this.dataset.minutes) || 20;
      endAt = endAt || read();

      if (endAt) {
        // Already running (or already spent) — synchronous, so a panel that
        // has expired is never painted.
        if (endAt - Date.now() > 0) start(minutes);
        return;
      }

      if (isOpen()) {
        start(minutes);
      } else {
        watchForOpen(minutes);
      }
    }
  }

  customElements.define('secret-offers', SecretOffers);
})();
